import { z } from "zod";
import { CITY_GUIDE_EXPERIENCE_LIMIT, CITY_GUIDE_GOING_OUT_LIMIT, CITY_GUIDE_VENUE_LIMIT, DEFAULT_HEADINGS, DEFAULT_IDENTITY_CELLS, DEFAULT_TEMPLATE_SECTIONS, IDENTITY_CELLS, SECTION_TYPE_IDS, SECTION_TYPES, } from "./city-page-sections.js";
import { CITY_PAGE_PREVIEW_COUNT } from "./landing.js";
/**
 * The settings each section accepts, and the resolver that turns a template and
 * one city's deltas into the sections a guide renders.
 *
 * This is the zod half of the section registry, kept in its own module because
 * the Zod-free half is what the guide's client bundle imports: the schemas are
 * built at module scope, so a public page that only needs section labels,
 * headings and anchors must never reach this file. The backend payload builder
 * and the dashboard studio — which do validate a composition — read it.
 */
/**
 * A heading glyph is a CHARACTER, or one of our own images when the value is a URL
 * we serve — never a file we do not, and never a `:shortcode:`.
 *
 * The studio's picker writes characters, which is the same kind of string `GOING
 * OUT` prints under its tiles. A shortcode is refused rather than resolved: the
 * custom emoji it names is an uploaded image whose public copy is created on
 * demand, so honouring it would put a stored-object read (and possibly a publish)
 * inside every public render for a decoration. A value in that shape is therefore
 * not a glyph at all — see `resolveCityPageGlyph`.
 */
const iconProp = { icon: z.string().trim().min(1).max(120).optional() };
/**
 * Where a section's glyph may point when it is an image: our own host.
 *
 * A picked glyph is a CHARACTER, so an image only ever arrives from someone
 * writing the API directly — and the rule for every public surface is that no
 * external image is hotlinked. An image we cannot serve ourselves is therefore not
 * rendered at all, the same fail-closed shape the poster rule uses. A relative path
 * is allowed because it can only resolve against the page's own origin.
 */
const GLYPH_MEDIA_HOST = /^(?:[a-z0-9-]+\.)*noko\.city$/i;
function glyphImageUrl(icon) {
    try {
        const url = new URL(icon, "https://noko.city/");
        return GLYPH_MEDIA_HOST.test(url.hostname) ? url.href : null;
    }
    catch {
        return null;
    }
}
/** A custom-emoji shortcode: the one shape this surface cannot print. */
const GLYPH_SHORTCODE = /^:[A-Za-z0-9_+-]+:$/;
export function resolveCityPageGlyph(icon) {
    if (!icon)
        return null;
    // Refused, not printed: a shortcode names an uploaded image whose public copy is
    // created on demand, and resolving it here would put a stored-object read inside
    // every public render. Printing the literal `:name:` beside a heading would be
    // worse than nothing, so an API writer that sends one gets no glyph — the same
    // fail-closed shape the poster rule uses.
    if (GLYPH_SHORTCODE.test(icon))
        return null;
    // A leading slash is root-relative: it stays as written so the browser resolves
    // it against the origin actually serving the document, rather than being baked
    // to production and pointing there from every dev, preview and test render.
    if (icon.startsWith("/") && !icon.startsWith("//")) {
        return { kind: "image", url: icon };
    }
    // A scheme or a protocol-relative pair means the value is meant to be an image;
    // anything else is the emoji character the picker writes.
    if (!/^[a-z][a-z0-9+.-]*:|^\/\//i.test(icon))
        return { kind: "text", text: icon };
    const url = glyphImageUrl(icon);
    return url === null ? null : { kind: "image", url };
}
/** A heading is text a person reads, so it is never blank and never a URL. */
const headingProp = { heading: z.string().trim().min(1).max(120).optional() };
const identityProps = z.object({
    cells: z
        .array(z.enum(IDENTITY_CELLS))
        .min(1)
        .max(IDENTITY_CELLS.length)
        .refine((cells) => new Set(cells).size === cells.length, {
        message: "A cell appears twice",
    })
        .default([...DEFAULT_IDENTITY_CELLS]),
});
const instagramProps = z.object({
    ...headingProp,
    limit: z.number().int().min(1).max(CITY_PAGE_PREVIEW_COUNT).default(CITY_PAGE_PREVIEW_COUNT),
    ...iconProp,
});
const experiencesProps = z.object({
    ...headingProp,
    limit: z
        .number()
        .int()
        .min(1)
        .max(CITY_GUIDE_EXPERIENCE_LIMIT)
        .default(CITY_GUIDE_EXPERIENCE_LIMIT),
    ...iconProp,
});
const goingOutProps = z.object({
    ...headingProp,
    limit: z
        .number()
        .int()
        .min(1)
        .max(CITY_GUIDE_GOING_OUT_LIMIT)
        .default(CITY_GUIDE_GOING_OUT_LIMIT),
    ...iconProp,
});
/** Sections with nothing to set: their content is authored, derived or fixed. */
const noProps = z.object({});
/** A section that prints a heading and has nothing else to set. */
const headedProps = z.object({ ...headingProp, ...iconProp });
/** The contact form prints its heading and carries no glyph of its own. */
const contactProps = z.object({ ...headingProp });
const venuesProps = z.object({
    ...headingProp,
    limit: z.number().int().min(1).max(CITY_GUIDE_VENUE_LIMIT).optional(),
    ...iconProp,
});
/** Each section's settings schema, keyed by the type it belongs to. */
export const SECTION_PROPS = {
    identity: identityProps,
    instagram: instagramProps,
    contents: noProps,
    history: headedProps,
    experiences: experiencesProps,
    "going-out": goingOutProps,
    essentials: headedProps,
    cost: headedProps,
    venues: venuesProps,
    contact: contactProps,
};
/**
 * One section's settings, defaults filled in from its own schema. This is the
 * single place the runtime schemas meet `SectionProps`, so the resolver, the
 * renderer and the studio never cast on their own.
 *
 * A value the section's schema refuses resolves to the section's own default
 * instead of throwing: a draft is validated when it is SAVED, and until then an
 * author is midway through typing — a limit above the cap or the last header cell
 * unchecked must not take the render path down, which would replace the studio
 * (and the unsaved draft) with the error screen. The authored schema reports the
 * issue to the author; here the section keeps the value it would have had. An
 * unknown key is dropped for the same reason: stored pages carry whatever a past
 * revision wrote, and a guide that stops parsing is a guide that stops serving.
 */
export function sectionProps(id, raw) {
    const parsed = SECTION_PROPS[id].safeParse(raw ?? {});
    return (parsed.success ? parsed.data : SECTION_PROPS[id].parse({}));
}
/**
 * The keys a section's settings accept, read from the schema itself: the studio
 * shows a field for each one, so its form cannot drift from what the resolver
 * consumes (and cannot grow a second list of which sections have a limit).
 */
export function sectionSettingKeys(id) {
    const schema = SECTION_PROPS[id];
    return Object.keys(schema.shape ?? {});
}
/**
 * The text a slot prints: the author's words, else what the section's own
 * settings say, else the code-owned copy.
 *
 * The middle layer of the chain — a value the section generates from its data —
 * has no members yet: every string in the guide is either authored or a heading.
 * A section that starts generating one adds it here, in one place, for every
 * consumer.
 */
export function resolveSlotText(id, config, slot) {
    if (!SECTION_TYPES[id].textSlots.includes(slot))
        return null;
    const override = config?.overrides?.[slot];
    if (override !== undefined && override.trim() !== "")
        return override;
    return slot === "heading"
        ? (sectionProps(id, config?.props).heading ?? DEFAULT_HEADINGS[id] ?? null)
        : null;
}
/** A city's deltas merged over the template's: the city says last, key by key. */
function mergeConfig(template, city) {
    const enabled = city?.enabled ?? template?.enabled;
    const props = { ...template?.props, ...city?.props };
    const overrides = { ...template?.overrides, ...city?.overrides };
    return {
        ...(enabled !== undefined ? { enabled } : {}),
        ...(Object.keys(props).length > 0 ? { props } : {}),
        ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
    };
}
/** True when an authored order names exactly the sections that will render. */
function isPermutationOf(order, ids) {
    if (order.length !== ids.length)
        return false;
    const seen = new Set(order);
    return seen.size === order.length && ids.every((id) => seen.has(id));
}
/**
 * The order the guide renders in: the author's own when it names exactly the
 * sections in play, else the template's. Pinned sections are then put where
 * their registry entry says — the hero at the top, the contact form at the end —
 * whatever an order claims about them, because those positions are structure.
 */
function orderedIds(ids, order) {
    const inPlay = order && isPermutationOf(order, ids) ? [...order] : [...ids];
    const first = SECTION_TYPE_IDS.filter((id) => SECTION_TYPES[id].pinned === "first" && inPlay.includes(id));
    const last = SECTION_TYPE_IDS.filter((id) => SECTION_TYPES[id].pinned === "last" && inPlay.includes(id));
    const middle = inPlay.filter((id) => !first.includes(id) && !last.includes(id));
    return [...first, ...middle, ...last];
}
function resolveSection(id, config) {
    const props = sectionProps(id, config.props);
    // An image we do not serve is dropped here rather than at the renderer, so the
    // payload never carries a URL the public page would have to refuse.
    const glyph = resolveCityPageGlyph(props.icon);
    return {
        id,
        // A locked section is not the author's to remove: the guide is not a guide
        // without its contents strip and its contact form.
        enabled: SECTION_TYPES[id].locked === true || config.enabled !== false,
        heading: resolveSlotText(id, config, "heading") ?? "",
        ...(glyph ? { icon: glyph.kind === "image" ? glyph.url : glyph.text } : {}),
        ...(props.limit !== undefined ? { limit: props.limit } : {}),
        ...(props.cells !== undefined ? { cells: props.cells } : {}),
    };
}
/**
 * The effective composition of one guide: the template's sections in their
 * order with each city's deltas merged over them, a type the template does not
 * carry but the city configures appended, the city's own order applied when it
 * names exactly the sections in play, and the pinned sections forced to the ends
 * they belong at.
 *
 * Called with nothing at all this reproduces the guide as it renders today —
 * `DEFAULT_RESOLVED_SECTIONS`, which a shared test holds equal to it — and
 * `template: null` means the registry's own order and defaults, so a database
 * with no template row still renders a complete guide.
 */
export function resolveSections(input) {
    const city = input.city ?? {};
    const template = input.template ?? DEFAULT_TEMPLATE_SECTIONS.map((id) => ({ id }));
    const ids = template.map((entry) => entry.id);
    for (const id of SECTION_TYPE_IDS) {
        if (!ids.includes(id) && city[id])
            ids.push(id);
    }
    const byId = new Map(template.map((entry) => [entry.id, entry]));
    return orderedIds(ids, input.sectionOrder ?? null).map((id) => resolveSection(id, mergeConfig(byId.get(id), city[id])));
}
//# sourceMappingURL=city-page-section-props.js.map